import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { useAdminNotificationsContext } from '../contexts/AdminNotificationsContext';
import { AdminNotification, AdminNotificationType } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

const TYPE_CHIP: Record<AdminNotificationType, { label: string; color: 'error' | 'warning' | 'info' }> = {
  LIST_NOT_CONFIGURED: { label: 'Não configurada', color: 'error' },
  LIST_MISCONFIGURED:  { label: 'Mal configurada', color: 'warning' },
  LIST_EXHAUSTED:      { label: 'Esgotada',         color: 'info' },
};

function parseCompanyId(dedupKey: string): string {
  return dedupKey.split(':')[1] ?? '';
}

function getFixPath(n: AdminNotification): string | null {
  switch (n.type) {
    case 'LIST_NOT_CONFIGURED':
    case 'LIST_MISCONFIGURED':
    case 'LIST_EXHAUSTED':
      return `/companies/${parseCompanyId(n.dedupKey)}/lists`;
    default:
      return null;
  }
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const AdminNotificationsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('notificacoes', 'editar');
  const { notifications, loading, error, resolveNotification } = useAdminNotificationsContext();
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Notificações do Sistema
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error">{error}</Typography>
      )}

      {!loading && !error && notifications.length === 0 && (
        <Typography color="text.secondary">Nenhuma notificação ativa</Typography>
      )}

      {!loading && !error && notifications.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Mensagem</TableCell>
                <TableCell align="center">Ocorrências</TableCell>
                <TableCell>Primeira ocorrência</TableCell>
                <TableCell>Última ocorrência</TableCell>
                <TableCell align="center">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map(n => {
                const chip = TYPE_CHIP[n.type];
                const fixPath = getFixPath(n);
                return (
                  <TableRow key={n.id} hover>
                    <TableCell>
                      <Chip label={chip.label} color={chip.color} size="small" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>{n.message}</TableCell>
                    <TableCell align="center">{n.occurrenceCount}</TableCell>
                    <TableCell>{formatDate(n.firstOccurrenceAt)}</TableCell>
                    <TableCell>{formatDate(n.lastOccurrenceAt)}</TableCell>
                    <TableCell align="center">
                      {fixPath && (
                        <Tooltip title="Corrigir">
                          <IconButton size="small" color="warning" onClick={() => navigate(fixPath)}>
                            <BuildIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={canManage ? 'Marcar como resolvido' : 'Você não tem permissão de edição no módulo Notificações.'}>
                        <span>
                          <IconButton size="small" color="success" disabled={!canManage} onClick={() => resolveNotification(n.id)}>
                            <CheckCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminNotificationsPage;
